import type { TFunction } from 'i18next'

import { formatDateTime } from '../../lib/format'
import type { BroadcastCounts, BroadcastRecipient, DraftAgent, MailMode } from './api'

/** Why the review screen cannot tick an agent — one sentence, the date of the last send when that is the reason. */
export function reasonText(agent: DraftAgent, t: TFunction, language: string): string | null {
  if (agent.reason === null) {
    return null
  }

  return t(`broadcasts.reasons.${agent.reason}`, {
    when: agent.lastSentAt === null ? '' : formatDateTime(agent.lastSentAt, language),
    defaultValue: t('broadcasts.reasons.unknown'),
  })
}

/**
 * What happened to one recipient's copy. Recorded is never said as sent, and sent is said as
 * accepted: acceptance by SES is not delivery.
 */
export function statusText(recipient: BroadcastRecipient, t: TFunction): string {
  const status = t(`broadcasts.statuses.${recipient.status}`, { defaultValue: recipient.status })

  if (recipient.status === 'Suppressed' && recipient.suppressedBecause !== null) {
    return `${status} — ${t(`broadcasts.suppressed.${recipient.suppressedBecause}`, {
      defaultValue: t('broadcasts.suppressed.unknown'),
    })}`
  }

  if (recipient.status === 'Failed' && recipient.failure !== null) {
    return `${status} — ${t(`broadcasts.failures.${recipient.failure}`, {
      defaultValue: t('broadcasts.failures.unknown'),
    })}`
  }

  return status
}

/** The badge class for a status: what went out or was recorded reads as done, the rest as what it is. */
export function statusTone(recipient: BroadcastRecipient): 'active' | 'pending' | 'blocked' {
  switch (recipient.status) {
    case 'Recorded':
    case 'Sent':
      return 'active'
    case 'Failed':
      return 'blocked'
    default:
      return 'pending'
  }
}

/** "2 recorded · 1 not sent" — only the statuses a broadcast has, in the order they happen. */
export function countsText(counts: BroadcastCounts, t: TFunction): string {
  const parts: string[] = []

  for (const [key, count] of [
    ['queued', counts.queued + counts.sending],
    ['recorded', counts.recorded],
    ['sent', counts.sent],
    ['suppressed', counts.suppressed],
    ['failed', counts.failed],
  ] as const) {
    if (count > 0) {
      parts.push(t(`broadcasts.counts.${key}`, { count }))
    }
  }

  return parts.join(' · ')
}

/** The mode as a label — "Recording (nothing is sent)". */
export function modeText(mode: MailMode, t: TFunction): string {
  return t(`broadcasts.modes.${mode}`, { defaultValue: mode })
}
